import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { PersonalConstants } from 'app/common/personalConstants';
import { BankAccountDetails } from '../models/payment-preferences.model';

@Injectable()
export class PaymentPreferencesFormService {
    constructor(private readonly fb: UntypedFormBuilder) {}

    buildForm(): UntypedFormGroup {
        return this.fb.group({
            paymentMethod: ['', Validators.required],
            bankAccountType: ['', Validators.required],
            accountName: ['', [Validators.required, this.accountNameValidator]],
            bankRoutingNumber: ['', [Validators.required, this.routingNumberValidator]],
            bankAccountNumber: ['', [Validators.required, Validators.pattern(/^\d{5,17}$/)]],
            confirmBankAccountNumber: ['', [Validators.required, Validators.pattern(/^\d{5,17}$/), this.confirmAccountValidator.bind(this)]]
        });
    }

    private paymentPreferencesForm: UntypedFormGroup | null = null;

    setFormReference(form: UntypedFormGroup): void {
        this.paymentPreferencesForm = form;
    }

    private confirmAccountValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value || !this.paymentPreferencesForm) {
            return null;
        }

        const accountNumber = this.paymentPreferencesForm.get('bankAccountNumber')?.value;
        return control.value === accountNumber ? null : { accountMismatchError: true };
    }

    private accountNameValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (!value) {
            return null;
        }

        if (PersonalConstants.regexValidators.isLeadingTrailingHyphenandSpaces.test(value)) {
            return { pattern: true };
        }

        const valid = PersonalConstants.regexValidators.isAccountNamePattern.test(value);
        return valid ? null : { accountNameInvalidError: true };
    }

    private routingNumberValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (!value) return null;

        if (!/^\d{9}$/.test(value)) {
            return { invalidRoutingNumber: true };
        }
        if (/^0+$/.test(value)) {
            return { invalidRoutingNumber: true };
        }
        if (value[0] === '5') {
            return { invalidRoutingNumber: true };
        }

        return null;
    }

    setupAccountNumberSync(form: UntypedFormGroup): void {
        form.get('bankAccountNumber')?.valueChanges.subscribe(() => {
            const confirmControl = form.get('confirmBankAccountNumber');
            if (confirmControl?.value) {
                confirmControl.updateValueAndValidity({ emitEvent: false });
            }
        });
    }

    populateFormFromAccount(form: UntypedFormGroup, account: BankAccountDetails): void {
        form.patchValue({
            paymentMethod: account.publicID,
            bankAccountType: account.bankAccountType ?? account.bankAccountingType ?? '',
            accountName: account.accountName,
            bankRoutingNumber: account.bankRoutingNumber,
            bankAccountNumber: account.bankAccountNumber,
            confirmBankAccountNumber: account.bankAccountNumber
        });
    }

    clearFormFields(form: UntypedFormGroup, fieldNames: string[]): void {
        fieldNames.forEach((fieldName) => {
            form.get(fieldName)?.reset();
        });
    }

    clearSensitiveFields(form: UntypedFormGroup): void {
        this.clearFormFields(form, ['bankAccountNumber', 'confirmBankAccountNumber']);
    }

    setValidators(form: UntypedFormGroup): void {
        const accountNameControl = form.get('accountName');
        const bankRoutingNumberControl = form.get('bankRoutingNumber');
        const bankAccountNumberControl = form.get('bankAccountNumber');
        const bankAccountTypeControl = form.get('bankAccountType');

        if (accountNameControl) {
            accountNameControl.setValidators([Validators.required, this.accountNameValidator]);
            accountNameControl.updateValueAndValidity();
        }

        if (bankRoutingNumberControl) {
            bankRoutingNumberControl.setValidators([Validators.required, this.routingNumberValidator]);
            bankRoutingNumberControl.updateValueAndValidity();
        }

        if (bankAccountNumberControl) {
            bankAccountNumberControl.setValidators([Validators.required, Validators.pattern(/^\d{5,17}$/)]);
            bankAccountNumberControl.updateValueAndValidity();
        }

        if (bankAccountTypeControl) {
            bankAccountTypeControl.setValidators([Validators.required]);
            bankAccountTypeControl.updateValueAndValidity();
        }
    }
}
